import { AgGridReact } from 'ag-grid-react'
import { useEffect, useRef, useState } from 'react'
import { Accordion, Col, Form, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import ReactSelect from 'react-select'
import AsyncSelect from 'react-select/async'
import { getLocaleText } from '../../../../actions/datagrid'
import DefaultDatePicker from '../../../../common/datepicker/DefaultDatePicker'
import { sideBar, statusBar } from '../../../../common/grid'
import {
	asyncSelectSearchSettings,
	obsoleteOptionStyle,
	reactSelectSearchSettings,
} from '../../../../common/reactselect'
import Switch from '../../../../common/Switch'
import { kevaMapper, selectMapper } from '../../../../common/utils'
import { authSelector } from '../../../../reducers'

export function EmploymentInfo({
	person,
	handleEmploymentDetails,
	handleEmploymentCodeItem,
	fieldParams,
	formErrors,
	employmentEnd,
	isPersonal,
	employmentHistory = [],
}) {
	const { t, i18n } = useTranslation()

	const auth = useSelector(authSelector)
	const occupationTypes = auth.company?.occupationTypes ?? 'MULTI'
	const gridRef = useRef()
	const prefix = 'employmentDetails'
	const [cbaCodes, setCbaCodes] = useState([])
	const [tk10, setTk10] = useState([])
	const [keva, setKeva] = useState([])

	const hasEmploymentEndedSection = Boolean(employmentEnd || person.employmentDetails?.endDate)

	const [activeAccordion, setActiveAccordion] = useState(() => {
		const arr = ['employmentInfo', 'employmentHistory']
		if (hasEmploymentEndedSection) arr.push('employmentEnded')
		return arr
	})

	const [prevHasEmploymentEndedSection, setPrevHasEmploymentEndedSection] = useState(hasEmploymentEndedSection)
	if (prevHasEmploymentEndedSection !== hasEmploymentEndedSection) {
		setPrevHasEmploymentEndedSection(hasEmploymentEndedSection)
		setActiveAccordion((prev) => {
			const next = [...prev]
			if (hasEmploymentEndedSection && !next.includes('employmentEnded')) {
				next.push('employmentEnded')
			}
			if (!hasEmploymentEndedSection) {
				return next.filter((k) => k !== 'employmentEnded')
			}
			return next
		})
	}

	useEffect(() => {
		async function fetchNamespaces() {
			await i18n.loadNamespaces(['cbacodes', 'tk10', 'keva'])

			setCbaCodes(
				Object.entries(i18n.getResourceBundle(i18n.resolvedLanguage, 'cbacodes') ?? {}).map(([key, value]) => {
					const isObsolete = value.startsWith('* ')
					return {
						value: key,
						label: `${key} - ${isObsolete ? value.slice(2) : value}`,
						isObsolete,
					}
				}),
			)

			setTk10(Object.entries(i18n.getResourceBundle(i18n.resolvedLanguage, 'tk10') ?? {}).map(selectMapper))

			// includes trafi codes (keva lists are only in fi,sv)
			// https://www.botsplash.com/post/optimize-your-react-select-component-to-smoothly-render-10k-data
			setKeva(Object.entries(i18n.getResourceBundle(i18n.resolvedLanguage, 'keva') ?? {}).map(kevaMapper))
		}
		fetchNamespaces()
	}, [i18n.resolvedLanguage, i18n.loadNamespaces, i18n.getResourceBundle])

	const loadOptions = (inputValue, callBack) => {
		const results = keva.filter((item) => item.label.toLowerCase().includes(inputValue.toLowerCase()))
		if (results.length <= 1000) callBack(results)
	}

	const customReactSelectSearchSettings = reactSelectSearchSettings(t)
	customReactSelectSearchSettings.styles = {
		...customReactSelectSearchSettings.styles,
		option: obsoleteOptionStyle, // gray out obsolete options in react-select dropdowns
		singleValue: obsoleteOptionStyle, // also gray out the selected value if it's obsolete
	}

	return (
		<>
			<Accordion
				flush
				activeKey={activeAccordion}
				onSelect={(eventKey) => {
					if (!eventKey) return
					setActiveAccordion((prev) =>
						prev.includes(eventKey) ? prev.filter((k) => k !== eventKey) : [...prev, eventKey],
					)
				}}
			>
				{(person.employmentDetails?.endDate || employmentEnd) && (
					<Accordion.Item key="employmentEnded" eventKey="employmentEnded">
						<Accordion.Header>{t('EmploymentEndedInfo', 'Työsuhteen päättämisen tiedot')}</Accordion.Header>
						<Accordion.Body className="d-flex flex-column">
							<Row className="align-items-end">
								<Form.Group as={Col} xl={8} controlId="endCode">
									<Form.Label>{t('EmploymentEndCode', 'Työsuhteen päättämisen syy')}</Form.Label>
									<Form.Select
										key={`endCode-${person.personId ?? 'new'}`}
										defaultValue={person.employmentDetails?.endCode}
										onChange={handleEmploymentDetails}
										disabled={isPersonal}
									>
										<option value="0">{t('Choose', 'Valitse')}</option>
										<option value="1">{t('EndCode1', 'Oma irtisanoutuminen')}</option>
										<option value="2">{t('EndCode2', 'Muu syy')}</option>
										<option value="3">{t('EndCode3', 'Henkilöstä riippuva syy')}</option>
										<option value="4">{t('EndCode4', 'Tuotannolliset ja taloudelliset syyt')}</option>
										<option value="5">{t('EndCode5', 'Työsuhteen päättyminen yhteisestä sopimuksesta')}</option>
										<option value="6">{t('EndCode6', 'Eläkkeelle siirtyminen')}</option>
										<option value="7">{t('EndCode7', 'Määräaikaisen työsuhteen päättyminen')}</option>
										<option value="10">{t('EndCode10', 'Kunta-ala: Eronnut, työ päättynyt')}</option>
										<option value="101">{t('EndCode101', 'Valtio: Eronnut, työ päättynyt')}</option>
									</Form.Select>
								</Form.Group>

								<Form.Group as={Col} xl={4} controlId="endDate">
									<DefaultDatePicker
										id={`${prefix}-endDate`}
										label={t('EmploymentEndDate', 'Työsuhde päättyy')}
										value={person.employmentDetails?.endDate}
										onChange={(iso) => handleEmploymentDetails({ target: { id: 'endDate', value: iso ?? '' } })}
										disabled={isPersonal}
									/>
								</Form.Group>
							</Row>
							<Row>
								<Form.Group as={Col} xl={12} controlId="endInfo">
									<Form.Label>{t('MessageToPayroll', 'Viesti palkanlaskentaan')}</Form.Label>
									<Form.Control
										as="textarea"
										rows={6}
										defaultValue={person.employmentDetails?.endInfo ?? ''}
										onChange={handleEmploymentDetails}
										disabled={isPersonal}
									/>
								</Form.Group>
							</Row>
						</Accordion.Body>
					</Accordion.Item>
				)}
				<Accordion.Item key="employmentInfo" eventKey="employmentInfo">
					<Accordion.Header>{t('EmploymentInfo', 'Työsuhdetiedot')}</Accordion.Header>
					<Accordion.Body className="d-flex flex-column">
						<Row>
							<Form.Group as={Col} xl={4} className="mt-2" controlId="startDate">
								<DefaultDatePicker
									id={`${prefix}-startDate`}
									name="startDate"
									label={t('EmploymentStartDate', 'Työsuhteen alkupäivä')}
									value={person.employmentDetails?.startDate}
									onChange={(iso) => handleEmploymentDetails({ target: { id: 'startDate', value: iso ?? '' } })}
									disabled={isPersonal}
								/>
							</Form.Group>
							<Form.Group as={Col} xl={4} className="mt-2" controlId="contractStartDate">
								<DefaultDatePicker
									id={`${prefix}-contractStartDate`}
									name="contractStartDate"
									label={t('NewContractStartDate', 'Uuden työsopimuksen alkupvm.')}
									value={person.employmentDetails?.contractStartDate}
									onChange={(iso) => handleEmploymentDetails({ target: { id: 'contractStartDate', value: iso ?? '' } })}
									disabled={isPersonal}
								/>
							</Form.Group>
						</Row>

						<Row>
							<Form.Group as={Col} xl={8} className="position-relative mt-3" controlId="fixedDuration">
								<Form.Label />
								<Form.Check
									inline
									type="radio"
									name="fixedDuration"
									label={t('UntilFurtherNotice', 'Toistaiseksi voimassaoleva')}
									id="fixedDuration.false"
									defaultChecked={!person.employmentDetails?.fixedDuration}
									onChange={handleEmploymentDetails}
									disabled={isPersonal}
								/>
								<Form.Check
									inline
									type="radio"
									name="fixedDuration"
									label={t('FixedDuration', 'Määräaikainen')}
									id="fixedDuration.true"
									defaultChecked={person.employmentDetails?.fixedDuration}
									onChange={handleEmploymentDetails}
									disabled={isPersonal}
								/>
							</Form.Group>
						</Row>

						<Row className="align-items-end">
							{person.employmentDetails?.fixedDuration && (
								<Form.Group as={Col} xl={4} className="position-relative mt-2" controlId="fixedDurationEndDate">
									<DefaultDatePicker
										id={`${prefix}-fixedDurationEndDate`}
										name="fixedDurationEndDate"
										label={t('EndofContract', 'Päättymisajankohta')}
										value={person.employmentDetails?.fixedDurationEndDate}
										onChange={(iso) =>
											handleEmploymentDetails({ target: { id: 'fixedDurationEndDate', value: iso ?? '' } })
										}
										disabled={isPersonal}
									/>
								</Form.Group>
							)}
							{person.employmentDetails?.fixedDuration && (
								<Form.Group as={Col} xl={8} className="position-relative" controlId="fixedDurationExplanation">
									<Form.Label>{t('FixedDurationExplanation', 'Määräaikaisuuden peruste')}</Form.Label>
									<Form.Control
										defaultValue={person.employmentDetails?.fixedDurationExplanation ?? ''}
										onChange={handleEmploymentDetails}
										disabled={isPersonal}
									/>
								</Form.Group>
							)}
							{!person.employmentDetails?.fixedDuration && (
								<Form.Group
									as={Col}
									xl={8}
									className="position-relative"
									controlId="fixedDurationExplanationPlaceholder"
								>
									<Form.Label></Form.Label>
								</Form.Group>
							)}
						</Row>

						<Row className="align-items-end">
							<Form.Group as={Col} xl={2} className="position-relative" controlId="trialPeriod">
								<Form.Label></Form.Label>
								<Form.Check
									type="checkbox"
									label={t('TrialPeriod', 'Sovelletaan koeaikaa')}
									defaultChecked={person.employmentDetails?.trialPeriod}
									onChange={handleEmploymentDetails}
									disabled={isPersonal}
								/>
								<Form.Label></Form.Label>
							</Form.Group>
							{person.employmentDetails?.trialPeriod && (
								<Form.Group as={Col} xl={2} className="position-relative" controlId="trialPeriodLengthDays">
									<Form.Label></Form.Label>
									<Form.Check
										type="checkbox"
										label={t('InDays', 'Päivinä')}
										defaultChecked={person.employmentDetails?.trialPeriodLengthDays}
										onChange={handleEmploymentDetails}
										disabled={isPersonal}
									/>
									<Form.Label></Form.Label>
								</Form.Group>
							)}
							{person.employmentDetails?.trialPeriod && (
								<>
									<Form.Group as={Col} xl={4} className="position-relative" controlId="trialPeriodLength">
										<Form.Label>
											{person.employmentDetails?.trialPeriodLengthDays
												? t('TrialPeriodLengthDays', 'Koeajan pituus päivinä')
												: t('TrialPeriodLength', 'Koeajan pituus kuukausina')}
										</Form.Label>
										<Form.Control
											type="number"
											min={1}
											step={1}
											defaultValue={person.employmentDetails?.trialPeriodLength ?? ''}
											onChange={handleEmploymentDetails}
											disabled={isPersonal}
										/>
									</Form.Group>
								</>
							)}
							{person.employmentDetails?.trialPeriod && (
								<>
									<Form.Group as={Col} xl={4} className="position-relative" controlId="trialPeriodEndDate">
										<DefaultDatePicker
											id={`${prefix}-trialPeriodEndDate`}
											name="trialPeriodEndDate"
											label={t('TrialPeriodEndDate', 'Koeaika päättyy')}
											value={person.employmentDetails?.trialPeriodEndDate}
											onChange={(iso) =>
												handleEmploymentDetails({ target: { id: 'trialPeriodEndDate', value: iso ?? '' } })
											}
											disabled={isPersonal}
										/>
									</Form.Group>
								</>
							)}
						</Row>
						{person.employmentDetails?.trialPeriod && (
							<>
								<Row>
									<Form.Group as={Col} xl={12} className="position-relative" controlId="trialPeriodExplanation">
										<Form.Label>{t('TrialPeriodExplanation', 'Koeaika selite')}</Form.Label>
										<Form.Control
											defaultValue={person.employmentDetails?.trialPeriodExplanation ?? ''}
											onChange={handleEmploymentDetails}
											disabled={isPersonal}
										/>
									</Form.Group>
								</Row>
							</>
						)}
						<Row>
							<Form.Group as={Col} xl={12} className="position-relative" controlId="termOfNotice">
								<Form.Label>{t('TermOfNotice', 'Irtisanomisaika')}</Form.Label>
								<Form.Control
									as="textarea"
									rows={3}
									defaultValue={person.employmentDetails?.termOfNotice ?? ''}
									onChange={handleEmploymentDetails}
									disabled={isPersonal}
								/>
							</Form.Group>
						</Row>

						<Row>
							<Form.Group as={Col} xl={12} className="position-relative" controlId="cbaCode">
								<Form.Label>{t('CbaCode', 'Sovellettava työehtosopimus')}</Form.Label>
								<ReactSelect
									name="cbaCode"
									inputId="cbaCode"
									isDisabled={isPersonal}
									options={cbaCodes}
									isClearable
									value={cbaCodes.find((option) => option.value === person.employmentDetails?.cbaCode)}
									onChange={handleEmploymentCodeItem}
									{...customReactSelectSearchSettings}
								/>
							</Form.Group>
						</Row>

						<Row>
							<Form.Group as={Col} xl={12} className="position-relative" controlId="place">
								<Form.Label>{t('Place', 'Työntekopaikka/alue')}</Form.Label>
								<Form.Control
									as="textarea"
									rows={3}
									defaultValue={person.employmentDetails?.place ?? ''}
									onChange={handleEmploymentDetails}
									disabled={isPersonal}
								/>
							</Form.Group>
						</Row>

						<Row>
							<Form.Group as={Col} xl={6} controlId="profession">
								<Form.Label>{t('JobTitle', 'Tehtävänimike')}</Form.Label>
								<Form.Control
									onChange={handleEmploymentDetails}
									defaultValue={person.employmentDetails?.profession}
									isInvalid={!!formErrors.profession}
									maxLength={fieldParams.profession?.maxLength}
									disabled={isPersonal}
								/>
								<Form.Control.Feedback type="invalid">{formErrors.profession}</Form.Control.Feedback>
							</Form.Group>
							<Form.Group as={Col} xl={6} className="position-relative" controlId="pensionSystem">
								<Form.Label>{t('PensionSystem', 'Eläkejärjestelmä')}</Form.Label>
								<Form.Control
									defaultValue={person.employmentDetails?.pensionSystem ?? ''}
									onChange={handleEmploymentDetails}
									disabled={isPersonal}
								/>
							</Form.Group>
						</Row>

						<Row>
							<Form.Group as={Col} xl={4} className="position-relative" controlId="occupationCodeTK10">
								{(occupationTypes === 'MULTI' || occupationTypes === 'TK10') && (
									<>
										<Form.Label>{t('OccupationCodeTK10', 'Ammattinimike (TK10)')}</Form.Label>
										<ReactSelect
											name="occupationCodeTK10"
											inputId="occupationCodeTK10"
											options={tk10}
											isClearable
											value={tk10.find((option) => option.value === person.employmentDetails?.occupationCodeTK10)}
											onChange={handleEmploymentCodeItem}
											isDisabled={isPersonal}
											{...reactSelectSearchSettings(t)}
										/>
									</>
								)}
							</Form.Group>
							<Form.Group as={Col} xl={4} className="position-relative" controlId="occupationCodeKeva">
								{(occupationTypes === 'MULTI' || occupationTypes === 'KEVA' || occupationTypes === 'TRAFI') && (
									<>
										<Form.Label>{t('OccupationCodeKeva', 'Ammattinimike (KEVA)')}</Form.Label>
										<AsyncSelect
											loadOptions={loadOptions}
											name="occupationCodeKeva"
											inputId="occupationCodeKeva"
											isClearable
											value={keva.find((option) => option.value === person.employmentDetails?.occupationCodeKeva)}
											onChange={handleEmploymentCodeItem}
											isDisabled={isPersonal}
											{...asyncSelectSearchSettings(t)}
										/>
									</>
								)}
							</Form.Group>
							<Form.Group as={Col} xl={4} className="mb-3" controlId="professionCodeAccidentInsurance">
								<Form.Label>{t('ProfessionCodeAccidentInsurance', 'Tapaturmavakuutuksen ammattikoodi')}</Form.Label>
								<Form.Control
									onChange={handleEmploymentDetails}
									defaultValue={person.employmentDetails?.professionCodeAccidentInsurance}
									isInvalid={!!formErrors.professionCodeAccidentInsurance}
									maxLength={fieldParams.professionCodeAccidentInsurance?.maxLength}
									disabled={isPersonal}
								/>
								<Form.Control.Feedback type="invalid">
									{formErrors.professionCodeAccidentInsurance}
								</Form.Control.Feedback>
							</Form.Group>
						</Row>
						<Row>
							<Form.Group as={Col} xl={12} className="position-relative" controlId="assignment">
								<Form.Label>{t('JobDescription', 'Työtehtävät')}</Form.Label>
								<Form.Control
									as="textarea"
									rows={3}
									defaultValue={person.employmentDetails?.assignment ?? ''}
									onChange={handleEmploymentDetails}
									disabled={isPersonal}
								/>
							</Form.Group>
						</Row>
						<Row>
							<Form.Group as={Col} xl={12} className="position-relative">
								<Switch
									className="mb-3"
									id="partOwner"
									label={t('PartOwner', 'Osaomistaja')}
									onChange={handleEmploymentDetails}
									defaultChecked={person.employmentDetails?.partOwner}
									disabled={isPersonal}
								/>
							</Form.Group>
						</Row>
					</Accordion.Body>
				</Accordion.Item>

				<Accordion.Item key="employmentHistory" eventKey="employmentHistory">
					<Accordion.Header>{t('EmploymentHistory', 'Työsuhdehistoria')}</Accordion.Header>
					<Accordion.Body>
						{employmentHistory.length === 0 ? (
							<p>{t('NoEmploymentHistory', 'Ei työsuhdehistoriaa')}</p>
						) : (
							<div className="ag-theme-material styles-ag flex-grow-1 mt-3">
								<AgGridReact
									rowData={employmentHistory}
									gridRef={gridRef}
									defaultColDef={{ sortable: true, filter: true, resizable: true, flex: 1 }}
									columnDefs={[
										{
											field: 'employmentStartDate',
											headerName: t('EmploymentStartDate', 'Työsuhteen alkupäivä'),
										},
										{
											field: 'contractStartDate',
											headerName: t('ContractStartDate', 'Sopimuksen alkupäivä'),
										},
										{
											field: 'contractEndDate',
											headerName: t('ContractEndDate', 'Sopimuksen loppupäivä'),
										},
										{
											field: 'contractType',
											headerName: t('ContractType', 'Sopimuksen tyyppi'),
											valueFormatter: ({ value }) =>
												value === 'fixedContract'
													? t('FixedContract', 'Määräaikainen')
													: t('PermanentContract', 'Toistaiseksi voimassa'),
										},
									]}
									domLayout="autoHeight"
									animateRows
									sideBar={sideBar}
									statusBar={statusBar}
									getLocaleText={(params) => getLocaleText(t, params)}
								/>
							</div>
						)}
					</Accordion.Body>
				</Accordion.Item>
			</Accordion>
		</>
	)
}
